import React from 'react';
import styled from 'styled-components';
import { Users, Target, Award } from 'lucide-react';
import { useSiteContent } from '../hooks/useSiteContent';

const Container = styled.section`
  margin-top: 90px;
  min-height: calc(100vh - 90px);
  background: var(--background-light);
`;

const Hero = styled.div`
  background: var(--primary-blue);
  color: var(--white);
  padding: 4rem 0;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-family: var(--font-secondary);
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  opacity: 0.9;
  max-width: 800px;
  margin: 0 auto;
`;

const Content = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 4rem 2rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 3rem;
`;

const Section = styled.div`
  background: var(--white);
  padding: 2.5rem;
  border-radius: 12px;
  box-shadow: var(--shadow-sm);
  border-top: 4px solid var(--gold);
`;

const SectionTitle = styled.h2`
  font-family: var(--font-secondary);
  font-size: 1.8rem;
  color: var(--primary-blue);
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const LeaderCard = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid var(--border-light);

  &:last-child {
    border-bottom: none;
  }
`;

const Avatar = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: var(--primary-blue);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--white);
  font-weight: bold;
  font-size: 1.2rem;
`;

const LeaderInfo = styled.div`
  flex: 1;
`;

const LeaderName = styled.h3`
  color: var(--text-dark);
  margin-bottom: 0.25rem;
`;

const LeaderRole = styled.p`
  color: var(--text-light);
  font-size: 0.9rem;
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
`;

const ListItem = styled.li`
  padding: 0.75rem 0;
  padding-left: 1.5rem;
  position: relative;
  color: var(--text-dark);
  line-height: 1.6;

  &::before {
    content: '•';
    position: absolute;
    left: 0;
    color: var(--gold);
    font-weight: bold;
    font-size: 1.2rem;
  }
`;

const Institution = () => {
  const { content } = useSiteContent('institution_page');
  const data = content || {};
  const leaders = Array.isArray(data.leaders) ? data.leaders : [];
  const missions = Array.isArray(data.missions) ? data.missions : [];
  const limits = Array.isArray(data.limits) ? data.limits : [];

  return (
    <Container>
      <Hero>
        <HeroTitle>{data.heroTitle || "L'Institution"}</HeroTitle>
        <HeroSubtitle>
          {data.heroSubtitle || "Autorité administrative indépendante investie d'une mission de service public de médiation"}
        </HeroSubtitle>
      </Hero>

      <Content>
        <Section>
          <SectionTitle>
            <Users size={24} />
            {data.leadersTitle || 'Équipe dirigeante'}
          </SectionTitle>
          {leaders.map((leader, idx) => (
            <LeaderCard key={`${leader.name || 'leader'}-${idx}`}>
              <Avatar>{leader.initials || '?'}</Avatar>
              <LeaderInfo>
                <LeaderName>{leader.name || ''}</LeaderName>
                <LeaderRole>{leader.role || ''}</LeaderRole>
              </LeaderInfo>
            </LeaderCard>
          ))}
        </Section>

        <Section>
          <SectionTitle>
            <Target size={24} />
            {data.missionsTitle || 'Rôle et missions'}
          </SectionTitle>
          <List>
            {missions.map((item, idx) => (
              <ListItem key={`mission-${idx}`}>{item}</ListItem>
            ))}
          </List>
        </Section>

        <Section>
          <SectionTitle>
            <Award size={24} />
            {data.limitsTitle || 'Pouvoirs et Limites'}
          </SectionTitle>
          <List>
            {limits.map((item, idx) => (
              <ListItem key={`limit-${idx}`}>{item}</ListItem>
            ))}
          </List>
        </Section>
      </Content>
    </Container>
  );
};

export default Institution;
